import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LuBuilding2,
  LuCalendar,
  LuDatabase,
  LuEye,
  LuFileText,
  LuPencil,
  LuPhone,
  LuReceipt,
  LuTrash2,
  LuUser,
} from "react-icons/lu";

import PurchaseStatusBadge from "../materialLines/PurchaseStatusBadge";
import Button from "../ui/Button";
import SortableTableHead from "../ui/SortableTableHead";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useConfirmPopup from "../../context/useConfirmPopup";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import {
  formatAmount,
  formatDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import {
  MaterialPurchaseListProps,
  MaterialPurchaseSortBy,
  MaterialPurchaseSortOrder,
} from "../../types/materialPurchase";

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const partyLinkClass = "font-semibold text-primary hover:underline break-words";

const contactIconClass = "h-3.5 w-3.5 flex-shrink-0 text-gray-400";

const detailPath = (id: number) =>
  route.materialPurchaseDetail.replace(":id", id as unknown as string);

const editPath = (id: number) =>
  route.materialPurchaseEdit.replace(":id", id as unknown as string);

const partyPath = (id: number) =>
  route.partyDetail.replace(":id", id as unknown as string);

// The contact name goes under the firm name; a party without a firm name shows its contact name in its place.
const contactName = (purchase: MaterialPurchaseListProps) =>
  purchase.party_firm_name ? purchase.party_name : null;

// Headers that also stand for other sorts: the date cell shows the purchase and creation dates, and the material cell
// the number of lines and the total qty.
const alsoSortsFor: Partial<
  Record<MaterialPurchaseSortBy, MaterialPurchaseSortBy[]>
> = {
  date: ["created_at", "updated_at"],
  items_count: ["total_qty"],
};

// The headers sort the list on the purchase list page; the table embedded on the material order page doesn't sort.
type SortProps = {
  // The order the purchases were loaded in.
  sortBy: MaterialPurchaseSortBy;
  sortOrder: MaterialPurchaseSortOrder;
  onSort: (
    sortBy: MaterialPurchaseSortBy,
    sortOrder: MaterialPurchaseSortOrder
  ) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<MaterialPurchaseSortBy, string>;
};

type Props = {
  purchases: MaterialPurchaseListProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeletePurchase: (id: number) => void;
  // Inside a detail card (the material order page): a bordered table with a tinted header row, like the reference app.
  embedded?: boolean;
  emptyLabel?: string;
  sort?: SortProps;
};

const defaultEmptyLabel = "મટીરીયલ ખરીદી ની વિગતો મળી નથી";

const MaterialPurchaseList = ({
  purchases,
  pageNumber,
  pageSize,
  isLoading,
  handleDeletePurchase,
  embedded = false,
  emptyLabel = defaultEmptyLabel,
  sort,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  // The material order page shows its purchases to users who can't open them.
  const canRead = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRead
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesDelete
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesReport
  );
  // The status and amount columns are only for a user who may see rates.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRate
  );

  const columnCount = canRate ? 8 : 6;
  const actionCount =
    (canRead ? 1 : 0) +
    (canReport ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો? પાર્ટી એકાઉન્ટ માંથી પણ આ ખરીદી નીકળી જશે.`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeletePurchase(id);
      setIsActionLoading(0);
    }
  };

  const handleExportReport = async (id: number) => {
    setIsActionLoading(id);
    const file = await makeReport(`/v1/admin/material-purchases/${id}/pdf`);
    setIsActionLoading(0);

    if (file) dispatch(updatePdfFile(file));
  };

  const sortHead = (
    column: MaterialPurchaseSortBy,
    label: string,
    className?: string
  ) =>
    sort ? (
      <SortableTableHead
        column={column}
        alsoFor={alsoSortsFor[column]}
        sortBy={sort.sortBy}
        sortOrder={sort.sortOrder}
        onSort={sort.onSort}
        sortLabels={sort.sortLabels}
        disabled={isLoading}
        className={className}
      >
        {label}
      </SortableTableHead>
    ) : (
      <TableHead className={cx(tableHeadCellClass, className)}>{label}</TableHead>
    );

  const purchaseNumber = (purchase: MaterialPurchaseListProps) => (
    <span className="inline-flex items-center gap-2 font-semibold text-primary">
      <LuReceipt className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <span>#{purchase.id}</span>
    </span>
  );

  const partyDetails = (
    purchase: MaterialPurchaseListProps,
    isMobile: boolean
  ) => {
    const contact = contactName(purchase);

    return (
      <div className={cx("min-w-0", isMobile && "text-right")}>
        <Link
          to={partyPath(purchase.party_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={partyLinkClass}
        >
          {purchase.party_firm_name || purchase.party_name || "-"}
        </Link>

        {(contact || purchase.party_phone) && (
          <div
            className={cx(
              "mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500",
              isMobile && "justify-end"
            )}
          >
            {contact && (
              <span className="flex items-center gap-1 break-words">
                <LuUser className={contactIconClass} />
                {contact}
              </span>
            )}

            {purchase.party_phone && (
              <span className="flex items-center gap-1">
                <LuPhone className={contactIconClass} />
                {purchase.party_phone}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const materialCount = (purchase: MaterialPurchaseListProps) =>
    `${formatQty(purchase.items_count)} મટીરીયલ`;

  const actionButtons = (
    purchase: MaterialPurchaseListProps,
    isMobile: boolean
  ) => {
    const buttonProps = isMobile
      ? { variant: "outline" as const, className: "w-full" }
      : { variant: "ghost" as const, size: "iconSm" as const };

    return (
      <>
        {canRead && (
          <Link to={detailPath(purchase.id)}>
            <Button {...buttonProps} title="વિગતો જુઓ">
              <LuEye className="h-4 w-4" />
            </Button>
          </Link>
        )}

        {canReport && (
          <Button
            {...buttonProps}
            title="રિપોર્ટ એક્સપોર્ટ"
            className={cx(
              buttonProps.className,
              "text-green-600 hover:text-green-700"
            )}
            onClick={() => handleExportReport(purchase.id)}
          >
            <LuFileText className="h-4 w-4" />
          </Button>
        )}

        {canUpdate && (
          <Button
            {...buttonProps}
            title="સંપાદિત કરો"
            onClick={() => navigate(editPath(purchase.id))}
          >
            <LuPencil className="h-4 w-4" />
          </Button>
        )}

        {canDelete && (
          <Button
            {...buttonProps}
            title="કાઢી નાખો"
            className={cx(
              buttonProps.className,
              "text-red-600 hover:text-red-700"
            )}
            onClick={() => handleDeleteRow(purchase.id)}
          >
            <LuTrash2 className="h-4 w-4" />
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <div
        className={cx(embedded ? detailTableWrapClass : cardClass, "max-sm:hidden")}
      >
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={embedded ? detailTableHeadRowClass : undefined}>
                  {/* ક્રમ and ખરીદી both stand for the purchase ID. */}
                  {sortHead("id", "ક્રમ", embedded ? "w-20" : undefined)}
                  {sortHead("id", "ખરીદી")}
                  {sortHead("party", "પાર્ટી")}
                  {sortHead("items_count", "મટીરીયલ")}
                  {canRate && (
                    <>
                      {sortHead("status", "સ્થિતિ")}
                      {sortHead("total", "રકમ", "text-right")}
                    </>
                  )}
                  {sortHead("date", "તારીખ")}
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    એકશન્સ બટન
                  </TableHead>
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={columnCount} />
              ) : purchases.length === 0 ? (
                <TableEmptyRow colSpan={columnCount} label={emptyLabel} />
              ) : (
                <TableBody>
                  {purchases.map((purchase, index) => (
                    <TableRow key={purchase.id}>
                      <TableCell className="font-semibold text-primary">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>{purchaseNumber(purchase)}</TableCell>

                      <TableCell>
                        <div className="flex items-start gap-2">
                          <LuBuilding2 className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                          {partyDetails(purchase, false)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium text-primary">
                          {materialCount(purchase)}
                        </div>
                        <div className="text-sm text-gray-500">
                          કુલ જથ્થો: {formatQty(purchase.total_qty)}
                        </div>
                      </TableCell>

                      {canRate && (
                        <>
                          <TableCell>
                            <PurchaseStatusBadge status={purchase.status ?? 1} />
                          </TableCell>

                          <TableCell className={moneyCellClass}>
                            {formatAmount(purchase.total)}
                          </TableCell>
                        </>
                      )}

                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDateTime(purchase.date)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(purchase.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isActionLoading === purchase.id ? (
                            <div className={actionSpinnerClass}></div>
                          ) : (
                            actionButtons(purchase, false)
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : purchases.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label={emptyLabel} />
          </div>
        ) : (
          purchases.map((purchase, index) => (
            <div key={purchase.id} className={mobileCardClass}>
              <div className="flex items-center justify-between gap-2 border-b p-4">
                {purchaseNumber(purchase)}
                {canRate && (
                  <PurchaseStatusBadge status={purchase.status ?? 1} />
                )}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પાર્ટી</span>
                  {partyDetails(purchase, true)}
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>મટીરીયલ</span>
                  <span className="text-right">
                    <span className="font-medium text-primary">
                      {materialCount(purchase)}
                    </span>
                    <span className="block text-sm text-gray-500">
                      કુલ જથ્થો: {formatQty(purchase.total_qty)}
                    </span>
                  </span>
                </div>

                {canRate && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>રકમ</span>
                    <span className={moneyCellClass}>
                      {formatAmount(purchase.total)}
                    </span>
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDateTime(purchase.date)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(purchase.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionGridClass[actionCount]
                  )}
                >
                  {isActionLoading === purchase.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    actionButtons(purchase, true)
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default MaterialPurchaseList;
